import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/api/client";
import { useOnline } from "@/hooks/useOnline";
import { useIntentKey } from "@/components/headjudge/useIntentKey";
import { useAuthStore } from "@/stores/auth";
import { useJudgeSession } from "@/stores/judgeSession";
import type { OfficialTransport } from "./shared";
import "./JudgeMessaging.css";

function currentPrincipal() {
  const auth = useAuthStore.getState();
  return JSON.stringify([
    auth.user?.id,
    auth.firebaseUser?.uid,
    useJudgeSession.getState().session?.principalKey,
  ]);
}

interface Presence {
  judge_count: number;
  items: {
    seat_id: string | null;
    judge_number: number;
    area_id: string | null;
    label: string | null;
    claimed: boolean;
    connected: boolean;
    stale: boolean;
  }[];
}
export function JudgeMessaging({
  competitionId,
  heatId,
  transport,
  connected = true,
}: {
  competitionId: string;
  heatId?: string;
  transport: OfficialTransport;
  connected?: boolean;
}) {
  const online = useOnline() && connected;
  const user = useAuthStore((s) => s.user?.id);
  const firebaseUser = useAuthStore((s) => s.firebaseUser?.uid);
  const session = useJudgeSession((s) => s.session?.principalKey);
  const principal = JSON.stringify([user, firebaseUser, session]);
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("");
  const [presence, setPresence] = useState<Presence | null>(null);
  const [presenceError, setPresenceError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [authorityBlocked, setAuthorityBlocked] = useState(false);
  const scope = JSON.stringify([competitionId, heatId, principal, body, target]);
  const keyFor = useIntentKey(scope);
  const current = useRef({ scope, online, mounted: true, transport });
  current.current = { scope, online, mounted: current.current.mounted, transport };
  const pending = useRef(false);
  useEffect(() => {
    current.current.mounted = true;
    return () => {
      current.current.mounted = false;
    };
  }, []);
  useEffect(() => {
    setPresence(null);
    setPresenceError("");
  }, [competitionId, heatId, principal, transport]);
  useEffect(() => {
    let active = true;
    if (!heatId || !online) return;
    const load = async () => {
      try {
        const next = await transport.get<Presence>(
          `/api/competitions/${competitionId}/judge-presence?heat_id=${encodeURIComponent(heatId)}`,
        );
        if (active) {
          setPresence(next);
          setPresenceError("");
        }
      } catch (e) {
        if (active)
          setPresenceError(e instanceof Error ? e.message : "Could not load judge presence.");
      }
    };
    void load();
    const timer = setInterval(() => void load(), 15_000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [competitionId, heatId, principal, transport, online, refresh]);
  async function verifyAuthority() {
    const snapshot = current.current;
    try {
      const next = await transport.get<Presence>(
        `/api/competitions/${competitionId}/judge-presence?heat_id=${encodeURIComponent(heatId ?? "")}`,
      );
      if (!current.current.mounted || current.current.scope !== snapshot.scope) return;
      setPresence(next);
      setPresenceError("");
      setAuthorityBlocked(false);
      setError("");
    } catch (e) {
      if (current.current.mounted && current.current.scope === snapshot.scope)
        setError(e instanceof Error ? e.message : "Could not verify access.");
    }
  }
  const validTarget = !target || (!!presence && Number(target) <= presence.judge_count);
  const disabled = !online || busy || authorityBlocked || !heatId;
  async function submit() {
    if (disabled || pending.current || !body.trim() || !validTarget) return;
    const payload = {
      heat_id: heatId,
      ...(target ? { to_judge_number: Number(target) } : {}),
      body: body.trim(),
    };
    const key = keyFor(payload);
    const snapshot = scope;
    const isCurrent = () =>
      current.current.mounted &&
      current.current.scope === snapshot &&
      current.current.transport === transport &&
      keyFor.isCurrent(key) &&
      currentPrincipal() === principal;
    const canRetry = () => isCurrent() && current.current.online && navigator.onLine;
    const accept = () => {
      if (!isCurrent()) return;
      keyFor.accept(key);
      setBody("");
      setError("");
      setNotice("Message sent.");
    };
    pending.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await transport.post(`/api/competitions/${competitionId}/judge-messages`, payload, {
        idempotencyKey: key,
        canSend: canRetry,
        canRetry,
        onRetrySuccess: accept,
        onConflict: () => {
          if (isCurrent()) {
            setAuthorityBlocked(true);
            setRefresh((v) => v + 1);
          }
        },
      });
      accept();
    } catch (e) {
      if (isCurrent()) {
        setError(e instanceof Error ? e.message : "Could not send. Your message has been kept.");
        if (e instanceof ApiError && [401, 403, 409].includes(e.status)) {
          keyFor.accept(key);
          setAuthorityBlocked(true);
          setRefresh((v) => v + 1);
        }
      }
    } finally {
      pending.current = false;
      if (current.current.mounted) setBusy(false);
    }
  }
  return (
    <section className="judge-messaging" aria-label="Judge messaging">
      {!heatId && <p>Select a heat to message its judges.</p>}
      {!online && (
        <p className="judge-message-notice" role="status">
          Offline — reconnect to message judges. Presence is unknown.
        </p>
      )}
      <h3>Judge presence</h3>
      {heatId && !presence && !presenceError && online && (
        <p role="status">Loading judge presence…</p>
      )}
      {presenceError && (
        <div role="alert">
          <p>Could not load judge presence. {presenceError}</p>
          <button type="button" disabled={!online} onClick={() => setRefresh((v) => v + 1)}>
            Retry presence
          </button>
        </div>
      )}
      {presence && (
        <ul className="judge-presence">
          {presence.items.map((seat) => {
            const status =
              !online || presenceError
                ? "Unknown"
                : seat.connected
                  ? "Online"
                  : seat.stale && seat.claimed
                    ? "Unknown · stale"
                    : "Offline";
            return (
              <li key={`${seat.area_id}:${seat.judge_number}`}>
                <strong>J{seat.judge_number}</strong>
                <span>{seat.label || (seat.claimed ? "Claimed seat" : "Unclaimed seat")}</span>
                <span className="judge-presence-status">
                  <span aria-hidden="true">{status === "Online" ? "●" : "○"}</span> {status}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {presence?.judge_count === 0 && <p>No judge seats in this panel.</p>}
      {error && (
        <p role="alert" className="judge-message-error">
          {error} Your message has been kept.
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {authorityBlocked && (
        <p role="alert">
          Authority changed. Verify access before sending. Your draft is kept here.
          <button type="button" disabled={!online} onClick={() => void verifyAuthority()}>
            Verify access
          </button>
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label>
          Send to
          <select
            value={target}
            disabled={disabled}
            onChange={(e) => {
              setTarget(e.target.value);
              setNotice("");
            }}
          >
            <option value="">All judges</option>
            {Array.from({ length: presence?.judge_count ?? 0 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                J{i + 1}
              </option>
            ))}
            {target && !validTarget && (
              <option value={target}>J{target} · no longer in panel</option>
            )}
          </select>
        </label>
        <label>
          Message
          <textarea
            value={body}
            required
            maxLength={2000}
            disabled={disabled}
            onChange={(e) => {
              setBody(e.target.value);
              setNotice("");
            }}
          />
        </label>
        <button
          type="submit"
          className="run-primary"
          disabled={disabled || !body.trim() || !validTarget || presence?.judge_count === 0}
        >
          {busy ? "Sending…" : "Send message"}
        </button>
      </form>
    </section>
  );
}
